import React from "react";
import { Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/Card";

const STATUS_TONE = {
  Pending: "amber", "Expiring Soon": "amber", "Half Day": "amber", "Ready for WPS": "amber",
  Approved: "green", Present: "green", Paid: "green",
  Rejected: "red", Absent: "red", Failed: "red", Expired: "red",
  Escalated: "violet", "On Leave": "violet", Processed: "blue",
  Cancelled: "slate", Draft: "slate", "Not Marked": "slate",
};

// Same palette as admin-panel statusColors / statusDotColors.
const TONES = {
  amber: ["bg-amber-100 text-amber-700", "bg-amber-500"],
  green: ["bg-teal-100 text-teal-700", "bg-teal-500"],
  red: ["bg-rose-100 text-rose-600", "bg-rose-500"],
  violet: ["bg-violet-100 text-violet-700", "bg-violet-500"],
  blue: ["bg-sky-100 text-sky-700", "bg-sky-500"],
  slate: ["bg-slate-100 text-slate-600", "bg-slate-400"],
  brand: ["", "bg-brand-orange"],
};

/** Inset pill, optionally tinted by tone, with a status dot. */
export function Badge({ tone = "brand", dot = true, className, children }) {
  const [colour, dotColour] = TONES[tone] || TONES.brand;
  return (
    <span className={cn("badge", colour, className)}>
      {dot && <span className={cn("w-1.5 h-1.5 rounded-full", dotColour)} />}
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  return <Badge tone={STATUS_TONE[status] || "slate"}>{status}</Badge>;
}

const WIDGET_COLOURS = {
  blue: { icon: "#225881", bg: "rgba(34,88,129,0.10)" },
  orange: { icon: "#D08C13", bg: "rgba(245,166,35,0.12)" },
  green: { icon: "#16a34a", bg: "rgba(22,163,74,0.10)" },
  red: { icon: "#dc2626", bg: "rgba(220,38,38,0.09)" },
};

/** Admin-panel StatWidget: recessed tile, tinted icon chip, coloured value. */
export function StatCard({ label, value, sub, icon: Icon, color, accent = false, onClick, className }) {
  const c = WIDGET_COLOURS[color || (accent ? "orange" : "blue")];
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={cn(
        "rounded-2xl p-4 flex flex-col gap-3 bg-surface shadow-neu-sm transition-all duration-200",
        onClick && "cursor-pointer hover:-translate-y-0.5 hover:shadow-neu",
        className,
      )}
    >
      <div className="flex items-center gap-2.5">
        {Icon && (
          <div className="w-9 h-9 rounded-xl relative shrink-0" style={{ background: c.bg }}>
            <Icon size={24} strokeWidth={1.8} style={{ color: c.icon }} className="absolute -right-2 -bottom-2" />
          </div>
        )}
        <p className={cn("text-xs font-semibold text-slate-400 leading-tight", Icon && "pl-2")}>{label}</p>
      </div>
      <div className="flex items-end justify-between gap-2">
        <p className="text-2xl font-extrabold tabular-nums leading-none tracking-tight" style={{ color: c.icon }}>{value}</p>
        {sub && <span className="text-[11px] font-semibold text-slate-400 leading-none mb-0.5 truncate">{sub}</span>}
      </div>
    </div>
  );
}

/** Recessed track with a coloured fill (admin-panel "Course Breakdown" bars). */
export function Progress({ value, className, barClassName = "bg-brand-orange" }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className={cn("h-2 rounded-full overflow-hidden neu-inset", className)}>
      <div className={cn("h-full rounded-full transition-all duration-700", barClassName)} style={{ width: `${pct}%` }} />
    </div>
  );
}

const GRADIENTS = ["from-teal-400 to-teal-600", "from-sky-400 to-sky-600", "from-amber-400 to-orange-500", "from-rose-400 to-rose-600", "from-emerald-400 to-emerald-600", "from-slate-400 to-slate-600"];
const AVATAR_SIZES = { xs: "w-6 h-6 text-xs", sm: "w-8 h-8 text-sm", md: "w-9 h-9 text-sm", lg: "w-10 h-10 text-base", xl: "w-14 h-14 text-lg", "2xl": "w-20 h-20 text-2xl" };

/** Gradient circle with the first initial, as in the admin panel. `brand` uses the blue brand gradient. */
export function Avatar({ name = "", size = "md", brand = false, className }) {
  const gradient = GRADIENTS[(name.charCodeAt(0) || 0) % GRADIENTS.length];
  return (
    <div
      className={cn("rounded-full flex items-center justify-center text-white font-semibold shrink-0", !brand && `bg-gradient-to-br ${gradient}`, AVATAR_SIZES[size], className)}
      style={brand ? { background: "linear-gradient(145deg,#225881,#1a4363)" } : undefined}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

/** Icon chip + label/value pair, used for profile and detail views. */
export function InfoItem({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex gap-3 min-w-0">
      {Icon && <div className="h-9 w-9 shrink-0 rounded-xl shadow-neu-inset-sm flex items-center justify-center text-brand-blue"><Icon size={15} /></div>}
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">{label}</p>
        <p className={cn("text-sm font-semibold text-brand-blue truncate mt-0.5", mono && "font-mono")}>{value || "—"}</p>
      </div>
    </div>
  );
}

/** Label/value rows for summaries. `rows` = [{label, value, className}] */
export function DefinitionList({ rows }) {
  return (
    <dl className="space-y-3 text-sm">
      {rows.map((r) => (
        <div key={r.label} className={cn("flex items-center justify-between gap-3", r.className)}>
          <dt className="text-slate-500">{r.label}</dt>
          <dd className="font-semibold text-brand-blue text-right">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EmptyState({ icon: Icon = Inbox, title = "Nothing here yet", message, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
      <div className="w-14 h-14 neu-inset flex items-center justify-center text-slate-400 mb-4"><Icon size={22} /></div>
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {message && <p className="text-xs text-slate-500 mt-1 max-w-xs">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loader({ label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <div className="w-5 h-5 border-2 border-brand-blue/30 border-t-brand-blue rounded-full animate-spin" />
      {label && <p className="text-xs text-slate-500">{label}</p>}
    </div>
  );
}

/**
 * Page toolbar card. The page title lives in the header (as in the admin panel),
 * so this carries the page's description, filters and primary actions.
 */
export function PageHeader({ description, actions, children }) {
  if (!description && !actions && !children) return null;
  return (
    <Card className="p-4 lg:p-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">
      {description && <p className="text-xs text-slate-500 flex-1 min-w-[200px]">{description}</p>}
      {children}
      {actions && <div className="flex items-end gap-2 flex-wrap sm:ml-auto">{actions}</div>}
    </Card>
  );
}
